/**
 * 할로윈 2026 커서 — 유령 컴패니언 (#6061 H3). 장식형이라 OS 커서는 그대로 두고 옆에 유령이 따라붙는다.
 *
 * 디자인 정본: Figma `Halloween 2026 — Cursor` (`31438:7383`) 「Spec · 움직임」 표 · 값과 로직은 디자인
 * 핸드오프 패키지(리포 밖)의 설정 · 커서 스크립트를 옮겼다.
 *
 * ```
 *   따라오기   커서 끝 기준 (+8, +8) 을 살짝 늦게 (보간 0.18)
 *   기울기     움직이는 방향으로 · 최대 10°
 *   둥실       ±2px · 0.8Hz(1.25초 주기) · sine
 *   클릭       유령 squash 0.26초 + 호박에서 사탕 3개 포물선 0.9초 · 연타 0.12초 제한
 *   표시 조건  데스크톱 + 마우스만(hover·fine pointer · pointerType mouse) · 터치 · 창 밖 · reduced-motion = 숨김
 * ```
 */
export const HALLOWEEN_2026_CURSOR = {
  size: { width: 32, height: 36 },
  offset: { x: 8, y: 8 },
  follow: 0.18,
  maxTilt: 10,
  bob: { amplitude: 2, hz: 0.8 },
  // 유령 이미지 안 호박(사탕 발사점) 위치 비율
  pumpkinAnchor: { x: 0.62, y: 0.62 },
  burst: {
    count: 3,
    duration: 900,
    gravity: 900, // px/s²
    angle: [-70, 20] as const, // deg (0 = 오른쪽 · 음수 = 위)
    speed: [220, 340] as const, // px/s
    spin: [180, 540] as const, // deg (방향 무작위)
    size: [18, 26] as const,
    throttle: 120,
  },
};

export const HALLOWEEN_2026_CURSOR_ASSETS = {
  companion: "/images/event/halloween-2026-cursor-companion@2x.png",
  candies: [
    "/images/event/halloween-2026-cursor-candy-wrap-1@2x.png",
    "/images/event/halloween-2026-cursor-candy-wrap-2@2x.png",
    "/images/event/halloween-2026-cursor-candy-corn@2x.png",
    "/images/event/halloween-2026-cursor-candy-lollipop@2x.png",
  ],
};

/** 레이어 표식 — 테스트 · 디버그에서 찾는다 */
export const CURSOR_LAYER_ATTR = "data-hw26-cursor";

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const clamp = (v: number, m: number) => Math.max(-m, Math.min(m, v));

/**
 * 유령 레이어를 `root` 에 붙인다 — 돌려준 함수로 뗀다(언마운트 · 기간 종료 · 결제 화면 진입).
 *
 * - 레이어는 `position: fixed` · `pointer-events: none` · `aria-hidden` — 클릭·스크린리더에 끼지 않는다.
 * - 터치 기기 · reduced-motion 이면 아예 붙이지 않고, 도중에 바뀌면 따라 떼고 붙인다.
 * - 보이는 동안만 rAF 를 돌린다(둥실이 계속 움직이므로) — 창 밖으로 나가거나 터치가 오면 멈춘다.
 */
export function mountCursorCompanion(
  root: HTMLElement,
  cfg = HALLOWEEN_2026_CURSOR,
  assets = HALLOWEEN_2026_CURSOR_ASSETS,
): () => void {
  if (typeof window.matchMedia !== "function") return () => {};
  const rm = window.matchMedia("(prefers-reduced-motion: reduce)");
  const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
  const { size, offset, bob, burst } = cfg;

  const layer = document.createElement("div");
  layer.setAttribute(CURSOR_LAYER_ATTR, "");
  layer.setAttribute("aria-hidden", "true");
  Object.assign(layer.style, {
    position: "fixed",
    inset: "0",
    pointerEvents: "none",
    zIndex: "2147483000",
    overflow: "hidden",
  });
  const comp = document.createElement("div");
  Object.assign(comp.style, {
    position: "absolute",
    left: "0",
    top: "0",
    width: `${size.width}px`,
    height: `${size.height}px`,
    opacity: "0",
    transition: "opacity 0.2s",
    willChange: "transform",
  });
  const img = new Image();
  img.alt = "";
  img.decoding = "async";
  img.src = assets.companion;
  Object.assign(img.style, {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  });
  comp.appendChild(img);
  layer.appendChild(comp);

  const s = { mx: 0, my: 0, x: 0, y: 0, tilt: 0, alive: false, t0: 0 };
  let raf = 0;
  let running = false;
  let lastBurst = -Infinity;
  let preloaded = false;

  const tick = (now: number) => {
    const dx = (s.mx + offset.x - s.x) * cfg.follow;
    const dy = (s.my + offset.y - s.y) * cfg.follow;
    s.x += dx;
    s.y += dy;
    // 움직이는 방향으로 기울기 (최대 maxTilt)
    s.tilt += (clamp(dx * 1.5, cfg.maxTilt) - s.tilt) * 0.2;
    const b =
      Math.sin(((now - s.t0) / 1000) * Math.PI * 2 * bob.hz) * bob.amplitude;
    comp.style.transform = `translate(${s.x.toFixed(2)}px, ${(s.y + b).toFixed(2)}px) rotate(${s.tilt.toFixed(2)}deg)`;
    raf = requestAnimationFrame(tick);
  };
  const show = () => {
    if (s.alive) return;
    s.alive = true;
    s.t0 = performance.now();
    s.x = s.mx + offset.x;
    s.y = s.my + offset.y;
    s.tilt = 0;
    comp.style.opacity = "1";
    raf = requestAnimationFrame(tick);
  };
  const hide = () => {
    s.alive = false;
    cancelAnimationFrame(raf);
    raf = 0;
    comp.style.opacity = "0";
  };

  const onMove = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") return hide();
    s.mx = e.clientX;
    s.my = e.clientY;
    show();
  };
  const onLeave = () => hide();

  // 사탕 버스트 — 포물선 키프레임 (WAAPI · transform/opacity 만)
  const squash = () =>
    img.animate?.(
      [
        { transform: "scale(1, 1)" },
        { transform: "scale(1.12, .88)" },
        { transform: "scale(.95, 1.06)" },
        { transform: "scale(1, 1)" },
      ],
      { duration: 260, easing: "ease-out" },
    );
  const toss = (ox: number, oy: number) => {
    const T = burst.duration / 1000;
    const steps = 10;
    for (let i = 0; i < burst.count; i++) {
      const el = document.createElement("div");
      const sz = rand(...burst.size);
      Object.assign(el.style, {
        position: "absolute",
        left: "0",
        top: "0",
        width: `${sz}px`,
        height: `${sz}px`,
      });
      const c = new Image();
      c.alt = "";
      c.src = assets.candies[Math.floor(Math.random() * assets.candies.length)];
      Object.assign(c.style, {
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
      });
      el.appendChild(c);
      layer.appendChild(el);
      const ang = (rand(...burst.angle) * Math.PI) / 180;
      const v = rand(...burst.speed);
      const vx = Math.cos(ang) * v;
      const vy = Math.sin(ang) * v;
      const spin = rand(...burst.spin) * (Math.random() < 0.5 ? -1 : 1);
      const kf: Keyframe[] = [];
      for (let k = 0; k <= steps; k++) {
        const p = k / steps;
        const t = p * T;
        const px = ox + vx * t - sz / 2;
        const py = oy + vy * t + (burst.gravity * t * t) / 2 - sz / 2;
        // 튀어나오며 커졌다가 서서히 작아짐
        const sc = p < 0.15 ? 0.5 + (p / 0.15) * 0.5 : 1 - (p - 0.15) * 0.25;
        kf.push({
          offset: p,
          transform: `translate(${px.toFixed(1)}px, ${py.toFixed(1)}px) rotate(${(spin * p).toFixed(1)}deg) scale(${sc.toFixed(3)})`,
          opacity: p < 0.65 ? 1 : 1 - (p - 0.65) / 0.35,
        });
      }
      const anim = el.animate?.(kf, {
        duration: burst.duration,
        easing: "linear",
        fill: "forwards",
      });
      if (anim) anim.onfinish = () => el.remove();
      else el.remove();
    }
  };
  const onDown = (e: PointerEvent) => {
    if (e.pointerType !== "mouse" || !s.alive) return;
    const now = performance.now();
    if (now - lastBurst < burst.throttle) return;
    lastBurst = now;
    squash();
    // 발사점 = 유령이 든 호박
    toss(
      s.x + cfg.pumpkinAnchor.x * size.width,
      s.y + cfg.pumpkinAnchor.y * size.height,
    );
  };

  const start = () => {
    if (running || rm.matches || !fine.matches) return;
    running = true;
    if (!preloaded) {
      // 사탕은 처음 클릭에 바로 보이도록 미리 받는다
      assets.candies.forEach((src) => {
        new Image().src = src;
      });
      preloaded = true;
    }
    root.appendChild(layer);
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);
  };
  const halt = () => {
    if (!running) return;
    running = false;
    hide();
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerdown", onDown);
    document.documentElement.removeEventListener("mouseleave", onLeave);
    // 날아가던 사탕 즉시 정리
    Array.from(layer.children).forEach((el) => {
      if (el === comp) return;
      el.getAnimations?.().forEach((a) => a.cancel());
      el.remove();
    });
    img.getAnimations?.().forEach((a) => a.cancel());
    layer.remove();
  };
  const onEnv = () => (rm.matches || !fine.matches ? halt() : start());

  rm.addEventListener?.("change", onEnv);
  fine.addEventListener?.("change", onEnv);
  start();
  return () => {
    rm.removeEventListener?.("change", onEnv);
    fine.removeEventListener?.("change", onEnv);
    halt();
  };
}
